import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { T, ENGAGEMENT_COLOURS } from "../theme";
import { proxyCall } from "../proxy";
import { parseStored, createSaver, LAST_MODIFIED } from "../persist";
import Banner from "../components/Banner";
import { StatusPill } from "../components/StatusPill";
import { orgName } from "../engagement";
import { shortDate, weekdayDate } from "../client/now";
import Pill, { STEP_TONE } from "../client/Pill";
import TimelineChart, { monthIndex } from "../components/TimelineChart";
import { sessionDots, chartStatus } from "../client/sessions";

// Thread's horizon: every dated roadmap step across every engagement, as a
// timeline (one row per engagement) and as a list by month. Each row links
// into the workspace's roadmap. Complete engagements are left out. Clicking
// a dot selects its step (ringed on the chart, highlighted in the list) and
// a step's status can be changed here: the write goes through the same
// read-before-write saver as the workspace, one per engagement.
const PHASES = ["Pre-engagement", "Activation", "In Flight"];
const HORIZON_MONTHS = 14; // this month plus the next 13 on the chart; the list runs to the last dated step
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ymAdd = (ym, n) => { const i = monthIndex(ym) + n; return `${Math.floor(i / 12)}-${String(i % 12 + 1).padStart(2, "0")}`; };
const monthLabel = ym => `${MONTHS_LONG[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

// Pure: the rows the chart and the list are built from.
// Demo engagements (Larkspur) are left out unless `demos` is set.
export function overviewRows(records, today, { demos = false } = {}) {
  return records
    .filter(r => r.fields.Status !== "Complete")
    .filter(r => demos || r.fields.Demo !== true)
    .map(r => {
      const stored = parseStored(r.fields);
      const steps = stored.steps || {};
      const dots = [];
      PHASES.forEach(phase => (steps[phase] || []).forEach(s => { if (s.date) dots.push({ ...s, phase }); }));
      const data = stored.data || {};
      const sessions = sessionDots(data.months, data.sessionBlock?.sessions, today).map(d => ({ ...d, phase: "Coaching", session: true }));
      return { key: r.fields.Slug, label: orgName(r.fields) || r.fields.Slug, slug: r.fields.Slug, dots, sessions };
    })
    .filter(r => r.dots.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((r, i) => ({ ...r, color: ENGAGEMENT_COLOURS[i % ENGAGEMENT_COLOURS.length] }));
}

// What each row is heading for: the first open dated step from today.
export function nextFor(row, today) {
  return row.dots.filter(d => !d.session && d.date >= today && d.status !== "complete").sort((a, b) => a.date.localeCompare(b.date))[0] || null;
}

export function upcomingItems(rows, today) {
  return rows
    .flatMap(r => r.dots.filter(d => d.date >= today && d.status !== "complete").map(d => ({ ...d, org: r.label, slug: r.slug })))
    .sort((a, b) => a.date.localeCompare(b.date) || a.org.localeCompare(b.org));
}

export default function Overview({ today = new Date().toLocaleDateString("en-CA") }) {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(false);
  const [saveError, setSaveError] = useState(null); // "stale" | "failed" | "readOnly"
  const [selected, setSelected] = useState(null);   // "slug:stepId"
  const [only, setOnly] = useState(null);           // slug filter, null = all
  const [withSessions, setWithSessions] = useState(false); // coaching sessions on the chart and in the list
  const [demos, setDemos] = useState(false);        // show demo engagements
  const savers = useRef({});
  useEffect(() => {
    proxyCall({ action: "list", table: "Engagements" }).then(d => {
      const recs = d.records || [];
      recs.forEach(r => { savers.current[r.id] = createSaver({ call: proxyCall, recordId: r.id, lastModified: r.fields[LAST_MODIFIED], readOnly: parseStored(r.fields).readOnly }); });
      setRecords(recs);
    }).catch(() => setError(true));
  }, []);
  const rows = useMemo(() => (records ? overviewRows(records, today, { demos }) : []), [records, today, demos]);
  const demoCount = (records || []).filter(r => r.fields.Demo === true && r.fields.Status !== "Complete").length;
  const shown = useMemo(() => {
    const base = only ? rows.filter(r => r.slug === only) : rows;
    return withSessions ? base.map(r => ({ ...r, dots: [...r.dots, ...r.sessions.map(d => ({ ...d, status: chartStatus(d.status) }))] })) : base;
  }, [rows, only, withSessions]);
  const items = useMemo(() => {
    const steps = upcomingItems(shown.map(r => ({ ...r, dots: r.dots.filter(d => !d.session) })), today);
    if (!withSessions) return steps;
    const sess = shown.flatMap(r => r.sessions.filter(d => d.date >= today).map(d => ({ ...d, org: r.label, slug: r.slug })));
    return [...steps, ...sess].sort((a, b) => a.date.localeCompare(b.date) || a.org.localeCompare(b.org));
  }, [shown, today, withSessions]);
  const thisMonth = today.slice(0, 7);
  const byMonth = [];
  items.forEach(it => {
    const ym = it.date.slice(0, 7);
    const last = byMonth[byMonth.length - 1];
    if (last && last.ym === ym) last.items.push(it); else byMonth.push({ ym, items: [it] });
  });
  const keyOf = (slug, id) => `${slug}:${id}`;
  const colourOf = slug => (rows.find(r => r.slug === slug) || {}).color;
  const selectedStep = selected ? rows.flatMap(r => r.dots.map(d => ({ ...d, org: r.label, slug: r.slug }))).find(d => keyOf(d.slug, d.id) === selected) : null;

  const select = (d, row) => {
    const k = keyOf(row.slug, d.id);
    setSelected(k);
    const el = document.getElementById(`ov-${row.slug}-${d.id}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Change one step's status: update the stored steps for that engagement
  // and save the whole Onboarding Steps blob, as the workspace does.
  const setStatus = async (slug, stepId, status) => {
    const rec = records.find(r => r.fields.Slug === slug);
    if (!rec) return;
    const steps = parseStored(rec.fields).steps || {};
    const next = Object.fromEntries(Object.entries(steps).map(([phase, list]) => [phase, list.map(st => (st.id === stepId ? { ...st, status } : st))]));
    const saver = savers.current[rec.id];
    let r;
    try { r = await saver.save({ "Onboarding Steps": JSON.stringify(next) }); } catch (e) { r = "failed"; }
    if (r !== "saved") { setSaveError(r); return; }
    setSaveError(null);
    setRecords(rs => rs.map(x => (x.id === rec.id ? { ...x, fields: { ...x.fields, "Onboarding Steps": JSON.stringify(next) } } : x)));
  };

  const Mark = ({ slug }) => <span aria-hidden="true" style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: colourOf(slug), marginRight: 7, verticalAlign: "baseline" }} />;
  const cell = { padding: "10px 8px 10px 0", borderTop: `1px solid ${T.borderSubtle}`, verticalAlign: "middle", fontSize: 14 };
  const StepRow = ({ it, highlight, anchor = true }) => (
    <tr id={anchor ? `ov-${it.slug}-${it.id}` : undefined} style={{ background: highlight ? T.bgSurfaceSoft : "transparent", transition: "background 0.3s" }}>
      <td style={{ ...cell, whiteSpace: "nowrap", color: T.fgSecondary, fontVariantNumeric: "tabular-nums" }}>{weekdayDate(it.date)}</td>
      <td style={{ ...cell, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}><Mark slug={it.slug} /><Link to={`/admin/${it.slug}#roadmap`} style={{ color: T.root, textDecoration: "none" }}>{it.org}</Link></td>
      <td style={{ ...cell, whiteSpace: "normal", overflow: "hidden", overflowWrap: "anywhere" }}><span style={{ fontWeight: 600, color: T.root }}>{it.label}</span><span style={{ color: T.fgMuted, fontSize: 13 }}> · {it.phase}{it.description ? ` · ${it.description}` : ""}</span></td>
      <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>{it.session ? <span style={{ fontSize: 12, color: T.fgMuted }}>Scheduled</span> : <StatusPill status={it.status || "to-start"} onChange={st => setStatus(it.slug, it.id, st)} />}</td>
    </tr>
  );
  const Table = ({ children }) => <div className="overview-table" style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}><colgroup><col style={{ width: 100 }} /><col style={{ width: 150 }} /><col /><col style={{ width: 232 }} /></colgroup><tbody>{children}</tbody></table></div>;

  return (
    <div className="hub-main" style={{ maxWidth: 960, margin: "0 auto", padding: "32px 40px 80px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.root, margin: 0 }}>What's coming</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {rows.some(r => r.sessions.length > 0) && <button onClick={() => setWithSessions(v => !v)} aria-pressed={withSessions} style={{ fontSize: 12, fontWeight: 600, padding: "4px 11px", borderRadius: T.radiusPill, border: `1px solid ${withSessions ? T.root : T.borderSubtle}`, background: withSessions ? T.root : "transparent", color: withSessions ? T.fgOnDark : T.fgSecondary, cursor: "pointer", fontFamily: T.fontSans, marginRight: 6 }}>Coaching sessions</button>}
        {rows.length > 1 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[{ slug: null, label: "All" }, ...rows].map(r => {
              const on = only === r.slug;
              return <button key={r.slug || "all"} onClick={() => setOnly(r.slug)} aria-pressed={on} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, padding: "4px 11px", borderRadius: T.radiusPill, border: `1px solid ${on ? T.root : T.borderSubtle}`, background: on ? T.root : "transparent", color: on ? T.fgOnDark : T.fgSecondary, cursor: "pointer", fontFamily: T.fontSans }}>{r.color && <span style={{ width: 9, height: 9, borderRadius: 2, background: r.color, display: "inline-block" }} />}{r.label}</button>;
            })}
          </div>
        )}
        </div>
      </div>
      {error && <p style={{ color: T.statusOff, fontSize: 14 }}>Couldn't load the engagements. Reload to try again.</p>}
      {saveError === "stale" && <div style={{ marginTop: 16 }}><Banner tone="warn" message="This page is out of date. Reload to continue." actionLabel="Reload" onAction={() => window.location.reload()} /></div>}
      {saveError === "failed" && <div style={{ marginTop: 16 }}><Banner tone="error" message="Couldn't save that change. Try again." /></div>}
      {saveError === "readOnly" && <div style={{ marginTop: 16 }}><Banner tone="warn" message="That engagement's roadmap is read-only until its stored steps are repaired in the workspace." /></div>}
      {records && rows.length === 0 && !error && <p style={{ fontSize: 15, color: T.fgSecondary }}>No dated roadmap steps yet. Dates added in each workspace's Roadmap show up here.</p>}
      {shown.length > 0 && (
        <section style={{ background: T.canvas, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, padding: "16px 20px 8px", marginTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.root, margin: 0 }}>The next {HORIZON_MONTHS - 1} months</h3>
            <span style={{ fontSize: 12, color: T.fgMuted }}>Filled = done · lime = in progress · outlined = to come. Click a dot to find it below.</span>
          </div>
          <TimelineChart rows={shown} today={today} from={ymAdd(thisMonth, -1)} to={ymAdd(thisMonth, HORIZON_MONTHS - 1)} labelWidth={120} rowHref={r => `/admin/${r.slug}#roadmap`} onDotClick={select} selectedId={selected} shadeMonth={thisMonth} noteWidth={170} rowNote={r => { const n = nextFor(r, today); if (!n) return "Nothing dated ahead"; const l = n.label.length > 22 ? n.label.slice(0, 21).trimEnd() + "…" : n.label; return `Next: ${l} · ${shortDate(n.date)}`; }} />
          {selectedStep && (
            <div style={{ borderTop: `1px solid ${T.borderSubtle}`, marginTop: 4, paddingTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.fgMuted, textTransform: "uppercase", letterSpacing: "0.06em", margin: "8px 0 0" }}>Selected</div>
              <Table><StepRow it={selectedStep} highlight={false} anchor={false} /></Table>
            </div>
          )}
        </section>
      )}
      {byMonth.length > 0 && (
        <Table>
          {byMonth.map(group => (
            <React.Fragment key={group.ym}>
              <tr><td colSpan={4} style={{ padding: "26px 0 4px", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: T.fgMuted }}>{monthLabel(group.ym)}</td></tr>
              {group.items.map(it => <StepRow key={`${it.slug}-${it.id}`} it={it} highlight={selected === keyOf(it.slug, it.id)} />)}
            </React.Fragment>
          ))}
        </Table>
      )}
      {demoCount > 0 && <div style={{ marginTop: 20, fontSize: 12 }}><button onClick={() => { setDemos(v => !v); setOnly(null); }} aria-pressed={demos} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: T.fontSans, fontSize: 12, fontWeight: 600, color: T.pine }}>{demos ? "Hide demo engagements" : `Show demo engagements (${demoCount})`}</button></div>}
      {records && rows.length > 0 && items.length === 0 && <p style={{ fontSize: 15, color: T.fgSecondary, marginTop: 20 }}>Nothing dated ahead of today.</p>}
    </div>
  );
}
